import React, {useState} from 'react';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    FormHelperText, Button,
} from '@mui/material';
import { FormikValues, useFormik } from 'formik';
import { facebookIntegrationUrl } from '../../../constants/integrationConstants';
import useGetOrganizationSettings from '../../../apiHooks/queries/useGetOrganizations';
import {
    GET_PROVIDER_DETAILS,
    ORGANIZATION_SETTINGS,
} from '../../../constants/pageIdentifiers';
import { AdProviderIDs, Colors } from '../../../constants/constants';
import SecondaryButton from '../../../components/buttons/secondaryButton';
import {
    useUpdateOrganizationSettings,
} from '../../../apiHooks/mutations/organizationMutations/organizationSettingsMutations';
import PrimaryButton from '../../../components/buttons/primaryButton';
import {
    facebookOrganizationSettingsValidationSchema,
} from '../../../validations/organizationSettingsFormValidationSchema';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import ProviderNumberBoxes from '../../../components/providerNumberBoxes/providerNumberBoxes';
import useGetAdProviderDetails from '../../../apiHooks/queries/adProviderDetails/useGetAdProviderDetails';
import useGetFacebookCustomers from "../../../apiHooks/queries/getFacebookCustomers/useGetFacebookCustomers";
import BasicDialog from "../../../dialogs/basicDialog";
import metaImage from '../../../assets/images/dashboard/metaModalImg.png'
import TextInput from "../../../components/inputs/textInput";
import useGetFacebookConversions from "../../../apiHooks/queries/facebookConversion/useGetFacebookConversion";
import {
    useSendDisconnectFacebookConversion, useSendFacebookAccessToken
} from "../../../apiHooks/mutations/facebookConversionToken/changeFacebookConversionToken";

const FacebookPanel = () => {
    const { t } = useTranslation();
    const { setOrganizationSettings } = useOrganizationSettingsStore();
    const [ tokenId , setTokenId ] = useState<string>("");
    const [ pixelId , setPixelId ] = useState<string>("");
    const [ openModal , setOpenModal ] = useState<boolean>(false);

    const {
        mutateAsync: updateOrganizationSettings,
        isLoading: isUpdatingOrganizationSettings,
    } = useUpdateOrganizationSettings();

    const { mutate : uninstallConversion } = useSendDisconnectFacebookConversion();
    const { mutate: sendAccessToken } = useSendFacebookAccessToken();


    const { data, refetch } = useGetOrganizationSettings(ORGANIZATION_SETTINGS);
    const { data: facebookCustomers } = useGetFacebookCustomers("GET_CUSTOMERS");
    const { data : facebookTokenId } = useGetFacebookConversions("FACEBOOK_CONVERSION");
    const organizationSettings = data?.data?.data;

    const facebookCustomerId = organizationSettings?.facebookCustomerId || '';
    const customerIdOptions = facebookCustomers?.data?.data || [];

    const formik: FormikValues = useFormik({
        initialValues: {
            facebookCustomerId: facebookCustomerId,
        },
        validationSchema: () =>
            facebookOrganizationSettingsValidationSchema(t),
        onSubmit: async (values: any) => {
            await updateOrganizationSettings(values);
            setOrganizationSettings({ ...organizationSettings, ...values });
        },
        enableReinitialize: true,
    });

    const { data: adProviderDetailsData, isFetching } = useGetAdProviderDetails(
        GET_PROVIDER_DETAILS,
        AdProviderIDs.FACEBOOK,
        !!organizationSettings?.facebookToken || false,
    );

    if (organizationSettings && organizationSettings.facebookToken) {
        return (
            <div className="flex flex-col items-end w-full">
                {!organizationSettings?.isConnectedToFacebookConversionApi &&
                <PrimaryButton
                    className="max-h-[40px] mb-4"
                    dataCy="bing-org-settings-submit"
                    onClick={() => setOpenModal(true)}
                >
                    {t('addTokenId')}
                </PrimaryButton>
                }
                <div className="flex flex-1 justify-between rounded-xl border p-4 w-full">
                    <form onSubmit={formik.handleSubmit} className="flex gap-4">
                        <Grid minWidth={300} item sm={12} md={6} lg={2.5}>
                            <FormControl
                                fullWidth
                                size="small"
                                error={Boolean(
                                    formik.touched.facebookCustomerId &&
                                    formik.errors.facebookCustomerId
                                )}
                            >
                                <InputLabel>{t('facebookCustomerId')}</InputLabel>
                                <Select
                                    label={t('facebookCustomerId')}
                                    name="facebookCustomerId"
                                    value={formik.values.facebookCustomerId}
                                    onChange={formik.handleChange}
                                >
                                    {customerIdOptions?.map((option: any) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {formik.touched.facebookCustomerId &&
                                        formik.errors.facebookCustomerId}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item sm={12} md={6} lg={2.5}>
                            <PrimaryButton
                                type="submit"
                                className="max-h-[40px]"
                                disabled={isUpdatingOrganizationSettings}
                                dataCy="bing-org-settings-submit"
                            >
                                {t('save')}
                            </PrimaryButton>
                        </Grid>
                    </form>

                    <div className="flex items-center justify-center gap-3 text-xs font-medium text-primary">
                        <div>{t('accountStatus')}</div>
                        <SecondaryButton
                            style={{
                                border: `1px solid ${Colors.green}`,
                                color: Colors.green,
                                height: '40px',
                                fontSize: 14,
                            }}
                        >
                            {t('active')}
                        </SecondaryButton>
                    </div>
                </div>

                <div className="mt-4 flex justify-between gap-4 rounded-xl border p-4 w-full">
                    <ProviderNumberBoxes
                        isLoading={isFetching}
                        tracked
                        stats={adProviderDetailsData?.data?.data?.ads_tracked || 0}
                    />
                    <ProviderNumberBoxes
                        isLoading={isFetching}
                        revenue
                        stats={adProviderDetailsData?.data?.data?.revenue_tracked || 0}
                    />
                    <ProviderNumberBoxes
                        isLoading={isFetching}
                        leads
                        stats={adProviderDetailsData?.data?.data?.leads_attributed || 0}
                    />
                </div>
                {organizationSettings?.isConnectedToFacebookConversionApi &&
                <div className="mt-4 flex justify-between items-center gap-4 rounded-xl border p-4 w-full">
                    <div className="text-sm font-medium text-primary flex flex-col gap-2">
                        <p className="font-semibold">{t('disconnectTokenId')}</p>
                        <p>{t('removeIntegration')}</p>
                    </div>
                    <button onClick={() => { uninstallConversion(); refetch(); }} className="bg-[#F8D8D6] text-[#DB3A34] rounded-[6px] px-8 py-3">{t('disconnectTokenId')}</button>
                </div>
                }
                <BasicDialog allowCloseOnBackdrop closeDialog={() => setOpenModal(false)} open={openModal} title={"addTokenId"}>
                    <div className="flex flex-col items-center gap-2">
                        <img src={metaImage} alt=""/>
                        <p className="text-primary text-center">{t('facebookAccessTokenReminder')}</p>
                    </div>
                    <div className="flex items-center gap-4 justify-center mt-4 pb-8">
                    <TextInput
                        name="Access Token"
                        value={tokenId}
                        label={t('accessToken')}
                        size="small"
                        placeholder={t('accessTokenPlaceholder')}
                        onChange={(e) => setTokenId(e.target.value)}
                    />
                        <TextInput
                            name="Pixel Token"
                            value={pixelId}
                            label={t('pixelToken')}
                            size="small"
                            placeholder={t('pixelTokenPlaceholder')}
                            onChange={(e) => setPixelId(e.target.value)}
                        />
                    <PrimaryButton
                        className="max-h-[40px]"
                        dataCy="bing-org-settings-submit"
                        onClick={() => { sendAccessToken({
                            tokenId: tokenId,
                            pixelId: pixelId,
                        }) ;
                            refetch(); setOpenModal(false); }}
                    >
                        {t('create')}
                    </PrimaryButton>
                    </div>
                </BasicDialog>
            </div>
        );
    }

    return (
        <div className="flex justify-between gap-5 rounded-xl border p-4">
            <div className="flex flex-col gap-2 text-primary">
                <span className="text-sm font-semibold">{t('connectToMeta')}</span>
                <p className="text-sm font-normal">
                    {t('metaPanelDescription')}
                </p>
            </div>
            <button
                type="button"
                className="flex items-center gap-2.5 rounded-lg bg-primary px-5 py-2
        text-sm text-white duration-200 focus:outline-[1px] focus:outline-offset-[3px] focus:outline-primary/50 enabled:hover:bg-[#0a3a4b]
        enabled:active:bg-opacity-80 disabled:opacity-50"
                onClick={() => {
                    window.location.href = facebookIntegrationUrl;
                }}
            >
                {t('connectToMeta')}
            </button>
        </div>
    );
};

export default FacebookPanel;
